import * as React from 'react'
import { clsx } from 'clsx'

import { IconButton } from '../IconButton'
import type { IconButtonProps } from '../IconButton'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '../icons'

import styles from './ScrollButton.module.css'

import type { JSX } from 'react'

interface ScrollButtonProps extends Omit<
  IconButtonProps,
  'label' | 'children'
> {
  direction: 'left' | 'right' | 'up' | 'down'
  onClick: () => void
  className?: string
}

const ScrollButton = React.forwardRef<HTMLButtonElement, ScrollButtonProps>(
  (
    { direction, onClick, className, ...props }: ScrollButtonProps,
    ref,
  ): JSX.Element | null => {
    const directionConfig = {
      left: { Icon: ChevronLeftIcon, className: styles.scrollButtonLeft },
      right: { Icon: ChevronRightIcon, className: styles.scrollButtonRight },
      up: { Icon: ChevronUpIcon, className: styles.scrollButtonUp },
      down: { Icon: ChevronDownIcon, className: styles.scrollButtonDown },
    } as const

    const config = directionConfig[direction]
    if (!config) {
      // TypeScript exhaustive check - this should never be reached
      throw new Error(`Unexpected direction: ${direction}`)
    }

    const { Icon, className: innerClassName } = config

    return (
      <IconButton
        ref={ref}
        label={`Scroll ${direction}`}
        size="small"
        variant="secondary"
        onClick={onClick}
        className={clsx(innerClassName, className)}
        type="button"
        {...props}
      >
        <Icon aria-hidden="true" focusable="false" />
      </IconButton>
    )
  },
)

ScrollButton.displayName = 'ScrollButton'

export { ScrollButton }
export type { ScrollButtonProps }
